// Desktop app notifications (requirement 13, Oct 2026). The installed app (Tauri) has no service worker and no push
// service, so the platform's Web Push cannot reach it. Instead, WHILE THE APP IS RUNNING (window open or minimised),
// it checks its own data every minute and shows native notifications (macOS Notification Center, Windows toasts)
// through the Rust commands notify_permission / notify_show (src-tauri/src/notify.rs):
//   appointments  N minutes before (the computer's own clock and time zone)
//   digest        once a day from 08:30: tasks / follow-ups due today and overdue, policies up for renewal (each once)
//   quiet hours   nothing is shown between «from» and «to»; an appointment still ahead is told when they end
// Nothing arrives while the app is closed — that is a limit of the desktop app, said in the settings card.
// Off until the person turns it on on this computer. Preferences, what was told and the last notices stay on this
// computer (localStorage, try/catch: a blocked storage only means the defaults and possibly a repeated notice).
import type { Task } from '@vero/domain';

export interface LocalReminderPrefs {
  on: boolean;
  appointments: boolean;
  minutesBefore: number;
  tasks: boolean;
  renewals: boolean;
  /** quiet hours, local "HH:MM" (may cross midnight); null = none */
  quietFrom: string | null;
  quietTo: string | null;
}
export const LOCAL_DEFAULTS: LocalReminderPrefs = { on: false, appointments: true, minutesBefore: 30, tasks: true, renewals: true, quietFrom: null, quietTo: null };
export const MINUTES_CHOICES = [10, 15, 30, 60, 120] as const;
export const DIGEST_FROM_MIN = 8 * 60 + 30;
export const DIGEST_UNTIL_MIN = 20 * 60;

export interface LocalReminder { key: string; kind: 'appointment' | 'digest'; title: string; body: string; /** keys to mark as told with it */ also?: string[] }
export interface LocalHistoryItem { at: string; kind: LocalReminder['kind'] | 'test'; title: string; body: string; ok: boolean }

const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;
const mins = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));
const pad = (n: number) => String(n).padStart(2, '0');
export const localDay = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export function cleanLocalPrefs(x: unknown): LocalReminderPrefs {
  const o = (x && typeof x === 'object' ? x : {}) as Record<string, unknown>;
  const b = (k: 'on' | 'appointments' | 'tasks' | 'renewals') => (typeof o[k] === 'boolean' ? o[k] as boolean : LOCAL_DEFAULTS[k]);
  const q = (k: 'quietFrom' | 'quietTo') => (typeof o[k] === 'string' && HHMM.test(o[k] as string) ? o[k] as string : null);
  const m = Number(o.minutesBefore);
  return { on: b('on'), appointments: b('appointments'), tasks: b('tasks'), renewals: b('renewals'), minutesBefore: (MINUTES_CHOICES as readonly number[]).includes(m) ? m : LOCAL_DEFAULTS.minutesBefore, quietFrom: q('quietFrom'), quietTo: q('quietTo') };
}
/** inside quiet hours at this local time */
export function localQuiet(now: Date, p: Pick<LocalReminderPrefs, 'quietFrom' | 'quietTo'>): boolean {
  if (!p.quietFrom || !p.quietTo || p.quietFrom === p.quietTo) return false;
  const m = now.getHours() * 60 + now.getMinutes(), f = mins(p.quietFrom), t = mins(p.quietTo);
  return f < t ? m >= f && m < t : m >= f || m < t;
}

export interface ReminderTexts {
  appointmentTitle: (min: number) => string;
  appointmentBody: (time: string, title: string) => string;
  digestTitle: string;
  due: (n: number) => string;
  overdue: (n: number) => string;
  renewals: (n: number) => string;
}
/** what to show now (pure: the caller shows them and marks their keys as told) */
export function dueLocalReminders(input: { now: Date; tasks: readonly Task[]; renewals: readonly { policyId: string; dueDate: string; days: number }[]; prefs: LocalReminderPrefs; told: (key: string) => boolean; texts: ReminderTexts }): LocalReminder[] {
  const { now, prefs, told, texts } = input;
  if (!prefs.on || localQuiet(now, prefs)) return [];
  const out: LocalReminder[] = [];
  const today = localDay(now);
  if (prefs.appointments) {
    for (const a of input.tasks) {
      if (a.kind !== 'appointment' || a.status !== 'open' || !a.dueDate || !a.dueTime) continue;
      const m = /^([01]\d|2[0-3]):([0-5]\d)/.exec(a.dueTime);
      if (!m) continue;
      const [y, mo, d] = a.dueDate.split('-').map(Number);
      const at = new Date(y!, mo! - 1, d!, Number(m[1]), Number(m[2])).getTime();
      const left = at - now.getTime();
      if (left <= 0 || left > prefs.minutesBefore * 60_000) continue;
      const key = `appt:${a.id}:${a.dueDate}T${a.dueTime}`;
      if (told(key)) continue;
      out.push({ key, kind: 'appointment', title: texts.appointmentTitle(Math.max(1, Math.round(left / 60_000))), body: texts.appointmentBody(a.dueTime.slice(0, 5), a.title) });
    }
  }
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const digestKey = `digest:${today}`;
  if ((prefs.tasks || prefs.renewals) && nowMin >= DIGEST_FROM_MIN && nowMin < DIGEST_UNTIL_MIN && !told(digestKey)) {
    let due = 0, overdue = 0;
    if (prefs.tasks) for (const x of input.tasks) {
      if (x.kind === 'appointment' || x.status !== 'open' || !x.dueDate || x.dueDate > today) continue;
      if (x.dueDate === today) due++; else overdue++;
    }
    const ren = prefs.renewals ? input.renewals.filter((r) => r.days >= 0 && r.days <= 30).map((r) => `renewal:${r.policyId}:${r.dueDate}`).filter((k) => !told(k)) : [];
    const lines = [[due ? texts.due(due) : '', overdue ? texts.overdue(overdue) : ''].filter(Boolean).join(' · '), ren.length ? texts.renewals(ren.length) : ''].filter(Boolean);
    // a day with nothing is still marked (an empty digest key), so it is not computed again every minute
    out.push(lines.length ? { key: digestKey, kind: 'digest', title: texts.digestTitle, body: lines.join('\n'), also: ren } : { key: digestKey, kind: 'digest', title: '', body: '' });
  }
  return out;
}

// --- this computer's storage ------------------------------------------------------------------------------------------
const K = { prefs: 'vero.localReminders.prefs', told: 'vero.localReminders.told', history: 'vero.localReminders.history' };
const read = <T>(k: string, d: T): T => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) as T : d; } catch { return d; } };
const write = (k: string, v: unknown) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* storage blocked */ } };
export const loadLocalPrefs = () => cleanLocalPrefs(read(K.prefs, {}));
export const saveLocalPrefs = (p: LocalReminderPrefs) => { write(K.prefs, p); window.dispatchEvent(new Event('vero:local-reminders')); };
export function toldStore(now = Date.now()) {
  const map = read<Record<string, number>>(K.told, {});
  for (const [k, t] of Object.entries(map)) if (now - t > 40 * 86_400_000) delete map[k];
  return { has: (k: string) => k in map, add: (k: string) => { map[k] = now; }, save: () => write(K.told, map) };
}
export const localHistory = (): LocalHistoryItem[] => read<LocalHistoryItem[]>(K.history, []);
export function addLocalHistory(item: LocalHistoryItem) { write(K.history, [item, ...localHistory()].slice(0, 50)); }

// --- native side (Tauri) ------------------------------------------------------------------------------------------------
type Invoke = (cmd: string, args?: Record<string, unknown>) => Promise<unknown>;
export function desktopInvoke(): Invoke | null {
  const w = (typeof window !== 'undefined' ? window : {}) as { __TAURI__?: { core?: { invoke?: Invoke } } };
  const inv = w.__TAURI__?.core?.invoke;
  return typeof inv === 'function' ? inv : null;
}
export const isDesktopApp = () => typeof globalThis !== 'undefined' && '__TAURI_INTERNALS__' in globalThis;
export type NativePermission = 'granted' | 'denied' | 'prompt' | 'unavailable';
export async function nativePermission(request: boolean): Promise<NativePermission> {
  const inv = desktopInvoke();
  if (!inv) return 'unavailable';
  try { const s = await inv('notify_permission', { request }); return s === 'granted' || s === 'denied' ? s : 'prompt'; } catch { return 'unavailable'; }
}
export async function showNative(title: string, body: string): Promise<boolean> {
  const inv = desktopInvoke();
  if (!inv) return false;
  try { await inv('notify_show', { title, body }); return true; } catch (e) { console.warn('[notify]', e); return false; }
}
